// Phase 7 — deterministic Proposal & Agreement content generation.
//
// Deliberately NOT a second pricing/entitlement source: every price, inclusion and
// exclusion below is read directly from `PLANS`/`getPlanPrice` in ./content.js. This file
// only arranges that same content into proposal/agreement document shape — nothing here
// invents a price, a scope item, or a commercial term that isn't already published on the
// live Plans pages. Section keys in AGREEMENT_SECTION_KEYS must match backend/server.py's
// AGREEMENT_SECTION_KEYS exactly (the backend validates against this list).
import { PLANS, PLAN_ADD_ONS, getPlanPrice, getRegionalPlan, SITE } from "./content";

export const AGREEMENT_SECTION_KEYS = [
  "parties_and_effective_date",
  "service_model_selected",
  "scope_of_included_services",
  "explicitly_excluded_services",
  "pricing_and_payment_terms",
  "payment_method_and_schedule",
  "edit_policy",
  "ownership_and_intellectual_property",
  "hosting_and_technical_responsibility",
  "third_party_services_disclaimer",
  "content_and_material_responsibility",
  "term_and_duration",
  "termination_and_access_removal",
  "post_handover_or_post_termination_options",
  "no_marketing_or_business_outcome_guarantees",
  "limitation_of_liability",
  "governing_law_and_jurisdiction",
  "acceptance_and_signatures",
];

export const LEGAL_REVIEW_NOTICE =
  "DRAFT — NOT LEGALLY REVIEWED. This agreement template has not been reviewed by a " +
  "qualified lawyer and must not be presented to a real client as a final, binding " +
  "document until it has been. Confirm applicable law, tax treatment, and any required " +
  "local disclosures before real use.";

export function planById(planId) {
  // Assisted Setup & Launch is a Website Ownership add-on, not a browsable plan
  // (see PLAN_ADD_ONS in ./content.js) — it still needs to resolve here so proposal/
  // agreement generation can produce correct, separate text for it as its own
  // billable line item.
  const plan = PLANS.find((p) => p.id === planId) || PLAN_ADD_ONS.find((p) => p.id === planId);
  if (!plan) throw new Error(`Unknown plan id: ${planId}`);
  return plan;
}

// The one-time-vs-ongoing commercial model, locked by MySiteCare and NOT to be
// reinterpreted here — see Terms.js §3 and the Phase 7 build notes.
const IS_ONE_TIME = { ownership: true, assisted_setup_launch: true, maintenance: false, responsibility: false, priority: false };

export function buildProposalContent(planId, region) {
  const plan = getRegionalPlan(planById(planId), region);
  const { price, period } = getPlanPrice(planId, region);
  return {
    plan_id: planId,
    region,
    proposal_title: `${plan.name} — Proposal for Review`,
    price_display: price,
    price_period: period,
    scope_included: plan.included,
    scope_excluded: plan.excluded || [],
    summary: plan.summary,
  };
}

function scopeLine(plan) {
  return plan.included.map((i) => `- ${i}`).join("\n");
}
function exclusionLine(plan) {
  return (plan.excluded || []).length ? plan.excluded.map((i) => `- ${i}`).join("\n") : "None beyond what is listed as included above.";
}

// Phase 7 Payment Methods — LOCKED (2026-08-23): bank_transfer, gcash, paymongo. "other"
// exists on the backend for exceptional manual recording only and is deliberately not
// given a client-facing label here.
const PAYMENT_METHOD_LABELS = {
  bank_transfer: "Bank Transfer", gcash: "GCash (manual/direct)", paymongo: "PayMongo (online)",
};

// Best-effort display labels for PayMongo's own sub-methods, if/when a real merchant
// account reports which ones are enabled. Falls back to a readable version of the raw
// code for anything not listed here — never a reason to hide a genuinely enabled method.
const PAYMONGO_SUBMETHOD_LABELS = {
  card: "Credit/Debit Card", gcash: "GCash", paymaya: "Maya", grab_pay: "GrabPay", qrph: "QR Ph", dob: "Online Banking",
};

// Builds one description sentence per ENABLED method only — a method absent from
// `enabled` gets no description at all, so disabled methods can never leak into the
// generated text even if their label constant exists.
function paymentMethodDescription(method, enabled, paymongoLabels) {
  if (!enabled.includes(method)) return null;
  if (method === "bank_transfer") return `Bank Transfer is recorded manually (amount, date, reference number and status); account details are provided separately by the Provider — never invented or assumed in advance.`;
  if (method === "gcash") return `GCash is recorded manually via direct transfer; only the transaction/reference number is recorded — no PINs, OTPs, or passwords are ever requested or stored.`;
  if (method === "paymongo") {
    return `PayMongo covers online payments — credit/debit card and any other method actually enabled on the Provider's PayMongo account ` +
      (paymongoLabels.length
        ? `(currently: ${paymongoLabels.join(", ")})`
        : `(the specific methods enabled are not yet configured — confirm current options with the Provider)`) +
      `. No PayMongo API keys, merchant IDs, or checkout links are generated or stored outside PayMongo's own system.`;
  }
  return null;
}

// enabledPaymentMethods and paymongoEnabledSubmethods must come from GET
// /admin/commercial-config — the backend's ENABLED_PAYMENT_METHODS /
// PAYMONGO_ENABLED_SUBMETHODS are the single source of truth for what MySiteCare is
// actually ready to publicly present; never hardcode a duplicate list here, and never
// assume every possible PayMongo method is available.
export function buildAgreementSections(planId, region, enabledPaymentMethods, paymongoEnabledSubmethods) {
  const plan = getRegionalPlan(planById(planId), region);
  const { price, period } = getPlanPrice(planId, region);
  const oneTime = IS_ONE_TIME[planId];
  const enabled = enabledPaymentMethods || [];
  const enabledLabels = enabled.map((m) => PAYMENT_METHOD_LABELS[m] || m);
  const paymongoLabels = (paymongoEnabledSubmethods || []).map((m) => PAYMONGO_SUBMETHOD_LABELS[m] || m);
  const methodDescriptions = ["bank_transfer", "gcash", "paymongo"]
    .map((m) => paymentMethodDescription(m, enabled, paymongoLabels))
    .filter(Boolean);

  const sections = {
    parties_and_effective_date:
      `This Service Agreement is between ${SITE.legalName} ("${SITE.name}", "the Provider") and the client business ` +
      `named on the accompanying proposal ("the Client"). It takes effect on the date both parties confirm acceptance ` +
      `below and applies to the plan selected: ${plan.name}.`,

    service_model_selected:
      planId === "ownership"
        ? `The Client is purchasing Website Ownership — a one-time, fully completed transaction covering the complete ` +
          `standalone website package. This is not a subscription and creates no continuing service relationship (see ` +
          `"Term & Duration" and "Post-Handover Options" below). Deployment and technical launch of the package are the ` +
          `Client's own responsibility unless Assisted Setup & Launch is purchased separately, together with or after this plan.`
        : planId === "assisted_setup_launch"
        ? `The Client is purchasing Assisted Setup & Launch — a one-time, optional service performed on top of a separately ` +
          `purchased Website Ownership package. It is not a website in itself and does not grant any ownership or ` +
          `intellectual property rights beyond what the Client's Website Ownership purchase already provides. This creates ` +
          `no continuing service relationship once the agreed setup and launch work is complete.`
        : planId === "maintenance"
        ? `The Client is purchasing the Maintenance Only plan — hosting, core software/plugin updates, and regular backups ` +
          `only. The Client remains fully responsible for the website's day-to-day operation, content and any issues not ` +
          `covered by this narrow scope. This plan must never be presented as a lower-cost substitute for the Responsibility ` +
          `Plan; it does not include monitoring, troubleshooting, or fixing breakages.`
        : planId === "responsibility"
        ? `The Client is purchasing the Responsibility Plan — the Provider accepts covered technical responsibility for ` +
          `hosting, updates, backups, uptime/system monitoring, and plan-covered update- or system-caused breakages, ` +
          `plus up to 30 minutes per month of small edits. This is a subscription for ongoing responsibility, not a rental ` +
          `of website features.`
        : `The Client is purchasing Priority & Reputation — everything included in the Responsibility Plan, plus priority ` +
          `handling, more proactive checks, and a higher reliability expectation. This plan does not include reputation ` +
          `management, review manipulation, or any SEO/search-ranking guarantee — none are offered by ${SITE.name}.`,

    scope_of_included_services: `The following is included under this plan:\n${scopeLine(plan)}`,

    explicitly_excluded_services:
      `Anything not explicitly listed as included above is excluded by default. In particular:\n${exclusionLine(plan)}`,

    pricing_and_payment_terms:
      `Price: ${price}${period ? ` ${period}` : ""}. ` +
      (region === "ph"
        ? `Applicable taxes, if any, are shown where required. `
        : `VAT, reverse-charge treatment, or other applicable tax treatment depends on the Client and transaction and is shown where required. `) +
      (oneTime
        ? `This is a one-time, non-recurring charge covering full delivery under this plan.`
        : `This is a recurring charge billed in advance for each period; late payment may result in suspension per the ` +
          `Provider's published Terms & Conditions.`),

    payment_method_and_schedule:
      `No card, bank, or payment-processor credentials are ever stored directly by the Provider's own systems. ` +
      (enabledLabels.length
        ? `Currently accepted payment methods: ${enabledLabels.join(", ")}. ${methodDescriptions.join(" ")}`
        : `Accepted payment methods have not yet been configured — confirm with the Provider directly.`) +
      ` The agreed payment method and any reference/receipt are recorded separately in the Client's commercial record ` +
      `at the time payment is made.`,

    edit_policy:
      planId === "responsibility" || planId === "priority"
        ? `Up to 30 minutes of small, non-structural, website-related edits per month are included. Unused time does not ` +
          `roll over to another month. Major work (redesigns, new features, substantial content buildouts) is out of scope ` +
          `and must be quoted separately.`
        : planId === "maintenance"
        ? `No edit allowance is included in Maintenance Only. Content or design changes are the Client's own responsibility ` +
          `or must be quoted as a separate job.`
        : planId === "assisted_setup_launch"
        ? `Not applicable — Assisted Setup & Launch is one-time deployment and launch labor, not an ongoing edit allowance.`
        : `Not applicable — Website Ownership is a completed, one-time delivery. Any change requested after handover is a ` +
          `new, separately quoted job (see "Post-Handover Options" below).`,

    ownership_and_intellectual_property:
      planId === "assisted_setup_launch"
        ? `Assisted Setup & Launch does not itself transfer any additional ownership or intellectual property beyond what ` +
          `the Client's separately purchased Website Ownership plan already grants. This is deployment and launch labor ` +
          `performed on the Client's own agreed infrastructure using the already-owned website package.`
        : oneTime
        ? `Full ownership of the delivered website files, and access to everything agreed as part of the handover package, ` +
          `transfers to the Client once payment is complete and the handover is delivered. Third-party assets (stock photos, ` +
          `fonts, plugins, libraries) remain subject to their own original licenses and are not re-licensed by this Agreement.`
        : region === "ph" && (planId === "responsibility" || planId === "priority")
        ? `Ownership is not transferred automatically under this subscription plan. Completed and fully paid qualifying ` +
          `months reduce the optional ownership-conversion price under the PH schedule. After 24 qualifying paid months, ` +
          `no additional MySiteCare Website Ownership purchase price is due, but verification, outstanding balances, ` +
          `third-party costs, a written request, and handover still apply. Until handover is completed, the website remains ` +
          `part of the active responsibility relationship.`
        : `Ownership is not transferred under this subscription plan. The website and MySiteCare-created transferable ` +
          `deliverables remain part of the service unless Website Ownership is separately purchased and handed over. ` +
          `Monthly payments do not, by themselves, create ownership rights.`,

    hosting_and_technical_responsibility:
      planId === "assisted_setup_launch"
        ? `${SITE.name} performs the agreed one-time setup and launch tasks — domain/DNS connection, hosting/database/` +
          `environment configuration, and connecting supported third-party services the Client has provided access to — ` +
          `and then has no further ongoing hosting or technical responsibility unless the Client separately holds an ` +
          `active care plan.`
        : oneTime
        ? `${SITE.name} has no ongoing hosting or technical responsibility for this website after handover is delivered. ` +
          `Where the Client continues to be hosted by ${SITE.name} temporarily as part of the handover process, that ` +
          `arrangement is described in the handover package itself and does not extend this Agreement.`
        : planId === "maintenance"
        ? `${SITE.name} provides hosting, core software/plugin updates, and regular backups only. Monitoring for problems, ` +
          `diagnosing issues, and fixing anything that breaks are explicitly the Client's own responsibility under this plan.`
        : `${SITE.name} is the Client's technical point of contact for the hosting, updates, backups, monitoring, and ` +
          `breakages covered by the accepted plan. Responsibilities outside that scope remain with the Client or the ` +
          `relevant third-party provider.`,

    third_party_services_disclaimer:
      `${SITE.name} is not responsible for the failure, downtime, policy changes, or discontinuation of any third-party ` +
      `service the website may depend on (e.g. domain registrars, hosting infrastructure, embedded maps, payment or booking ` +
      `widgets, social platforms). Costs charged directly by third parties are the Client's own responsibility unless ` +
      `explicitly stated otherwise in the proposal.`,

    content_and_material_responsibility:
      `The Client is responsible for the accuracy and legality of all business information, images, and other material it ` +
      `supplies or approves for the website, and for keeping ${SITE.name} informed of any required corrections.`,

    term_and_duration:
      oneTime
        ? `This Agreement covers a single, completed delivery. It has no ongoing term and does not renew.`
        : `This Agreement remains in effect for as long as the subscription is active and paid, billed and renewed on a ` +
          `monthly basis unless cancelled by either party.`,

    termination_and_access_removal:
      oneTime
        ? `Not applicable in the ongoing sense — once handover is delivered and paid in full, there is nothing further to ` +
          `terminate. If a temporary hosting arrangement was part of the handover, its own end date applies instead.`
        : `Access to the live website may be removed on termination or non-payment unless Website Ownership has separately ` +
          `been purchased, per ${SITE.name}'s published Terms & Conditions.`,

    post_handover_or_post_termination_options:
      planId === "assisted_setup_launch"
        ? `${SITE.name} has no continuing obligation once the agreed setup and launch work is complete and verified. ` +
          `Should the Client want further work later, the available paths are: (1) a separately quoted one-off job, or ` +
          `(2) an appropriate monthly plan (Maintenance Only, Responsibility, or Priority & Reputation) — never assumed, ` +
          `always by the Client's own request.`
        : oneTime
        ? `${SITE.name} has no continuing obligation after a completed Website Ownership handover. Should the Client want ` +
          `further work later, the available paths are: (1) a separately quoted one-off job, (2) purchasing Assisted Setup ` +
          `& Launch if deployment help is what's needed, or (3) migrating onto an appropriate monthly plan (Maintenance ` +
          `Only, Responsibility, or Priority & Reputation) — never assumed, always by the Client's own request.`
        : region === "ph" && (planId === "responsibility" || planId === "priority")
        ? `If the Client wishes to stop the subscription, the Client may request ownership conversion using the qualifying ` +
          `paid-month schedule recorded in the accepted Order, or may request separately quoted work. Ownership never ` +
          `transfers without the required written handover process.`
        : `If the Client wishes to stop the subscription, options include requesting a separate quote for outstanding needs ` +
          `or purchasing Website Ownership outright, subject to its own current terms and pricing.`,

    no_marketing_or_business_outcome_guarantees:
      `${SITE.name} does not guarantee leads, sales, rankings, or any specific business outcome from this website. No ` +
      `marketing, SEO ranking, or reputation-management promises are made under this Agreement beyond what is explicitly ` +
      `listed as included above.`,

    limitation_of_liability:
      `${SITE.name}'s liability under this Agreement is limited to the amount actually paid by the Client for the plan in ` +
      `question, to the fullest extent permitted by applicable law. ${SITE.name} is not liable for indirect, incidental, or ` +
      `consequential losses.`,

    governing_law_and_jurisdiction:
      region === "ph"
        ? `This Agreement is governed by the laws of the Republic of the Philippines, subject to mandatory rights, remedies, ` +
          `regulatory processes, consumer protections, privacy remedies, and jurisdiction rules that cannot be excluded.`
        : `For a business-to-business Order, the parties intend Belgian law to apply where a valid choice of law is permitted, ` +
          `with disputes handled by the competent courts identified in the accepted Order or determined by applicable ` +
          `jurisdiction rules. Mandatory law and non-excludable rights continue to apply.`,

    acceptance_and_signatures:
      `By accepting this Agreement (recorded in the Client's onboarding record with the accepting person's name and the ` +
      `agreement version), both parties confirm they have read and agree to the terms above.`,
  };

  return sections;
}
